"use client";

import React, { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import {
  MoreHorizontal,
  ArrowUpDown,
  FileText,
  Eye,
  RefreshCw,
  Download,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ConfirmationDialog } from "../components/confirmationDialog";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import axios from "axios";

// Updated type with off-boarding fields
export type PayslipEmployee = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  designation?: string;
  department?: string;
  hasAttendance: boolean;
  payslipStatus: "not_generated" | "generated";
  isActive: boolean;
  resignationDate?: string;
  attendance?: {
    month: number;
    year: number;
    workingDays: number;
    paidLeaves: number;
    unpaidLeaves: number;
  };
  payslip?: {
    id?: string;
    netSalary: number;
    status: "pending" | "approved";
    s3Url: string;
    salaryComponents?: any;
  };
};

// Enhanced PayslipActionsCell component with better loading states
const PayslipActionsCell = ({
  employee,
  month,
  year,
  onUpdate,
}: {
  employee: PayslipEmployee;
  month: string;
  year: string;
  onUpdate: () => void;
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);

  const handleGeneratePayslip = async () => {
    try {
      setIsGenerating(true);
      const token = localStorage.getItem("authToken");

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/payslips/generate/${employee.id}`,
        {
          month: parseInt(month),
          year: parseInt(year),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      onUpdate();
    } catch (error: any) {
      console.error("Error generating payslip:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprovePayslip = async () => {
    try {
      setIsApproving(true);
      const token = localStorage.getItem("authToken");

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/payslips/approve/employee/${employee.id}`,
        {
          month: parseInt(month),
          year: parseInt(year),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setApprovalDialogOpen(false);
      onUpdate();

      alert(
        `✅ Success! ${
          response.data.message ||
          "Payslip approved and email sent to the employee."
        }`
      );
    } catch (error: any) {
      console.error("Error approving payslip:", error);

      if (error.response?.status === 500 && error.response?.data?.error) {
        alert(
          `❌ ${error.response.data.error}\n\nThe payslip was not approved. Please try again.`
        );
      } else {
        alert("❌ Error approving payslip. Please try again.");
      }
    } finally {
      setIsApproving(false);
    }
  };

  const handleViewPayslip = async () => {
    if (!employee.payslip?.s3Url) return;
    setIsViewing(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      window.open(employee.payslip.s3Url, "_blank");
    } finally {
      setIsViewing(false);
    }
  };

  const handleDownloadPayslip = async () => {
    if (!employee.payslip?.s3Url) return;
    setIsDownloading(true);
    try {
      // Fetch the file as a blob to force download instead of opening in new tab
      const token = localStorage.getItem("authToken");
      let response;
      
      try {
        // Try with authentication header first
        response = await axios.get(employee.payslip.s3Url, {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (authError) {
        // If that fails, try without auth (for pre-signed URLs or public URLs)
        response = await axios.get(employee.payslip.s3Url, {
          responseType: "blob",
        });
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `payslip_${employee.name}_${month}_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error downloading payslip:", error);
      alert("Failed to download payslip. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {employee.payslipStatus === "not_generated" ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                disabled={isGenerating}
              >
                <span className="sr-only">Open menu</span>
                {isGenerating ? (
                  <Spinner variant="ellipsis" size={16} />
                ) : (
                  <MoreHorizontal className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleGeneratePayslip}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                ) : (
                  <FileText className="mr-2 h-4 w-4" />
                )}
                {isGenerating ? "Generating..." : "Generate Payslip"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-8 w-8 p-0"
                disabled={isApproving || isViewing || isDownloading}
              >
                <span className="sr-only">Open menu</span>
                {isApproving || isViewing || isDownloading ? (
                  <Spinner variant="ellipsis" size={16} />
                ) : (
                  <MoreHorizontal className="h-4 w-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleViewPayslip}
                disabled={isViewing}
              >
                {isViewing ? (
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                ) : (
                  <Eye className="mr-2 h-4 w-4" />
                )}
                {isViewing ? "Opening..." : "View Payslip"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleDownloadPayslip}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                {isDownloading ? "Downloading..." : "Download"}
              </DropdownMenuItem>
              {employee.payslip?.status === "pending" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setApprovalDialogOpen(true)}
                    disabled={isApproving}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Approve Payslip
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <ConfirmationDialog
        isOpen={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        onConfirm={handleApprovePayslip}
        title="Approve Payslip"
        description={`Are you sure you want to approve the payslip for ${employee.name}? An email will be sent to the employee.`}
        confirmText={isApproving ? "Approving..." : "Approve"}
        isLoading={isApproving}
      />
    </>
  );
};

export const payslipColumns = (
  month: string,
  year: string,
  onUpdate: () => void
): ColumnDef<PayslipEmployee>[] => [
  {
    accessorKey: "name",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Employee Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const name = row.getValue("name") as string;
      return <div className="font-medium text-left">{name}</div>;
    },
  },
  {
    accessorKey: "email",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Email
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const email = row.getValue("email") as string;
      return <div className="text-muted-foreground text-left">{email}</div>;
    },
  },
  {
    accessorKey: "isActive",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Employee Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const isActive = row.getValue("isActive") as boolean;
      const resignationDate = row.original.resignationDate;

      return (
        <div className="text-left">
          {isActive ? (
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-green-100 text-green-800">
              Active
            </span>
          ) : (
            <div className="space-y-1">
              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-red-100 text-red-800">
                Inactive
              </span>
              {resignationDate && (
                <div className="text-xs text-muted-foreground">
                  {new Date(resignationDate).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "payslipStatus",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-auto p-0 justify-start"
        >
          Payslip Status
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const status = row.getValue("payslipStatus") as string;
      const employee = row.original;

      if (status === "generated" && employee.payslip) {
        if (employee.payslip.status === "approved") {
          return (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-green-100 text-green-800">
                <CheckCircle className="mr-1 h-3 w-3" />
                Approved
              </span>
            </div>
          );
        } else {
          return (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-yellow-100 text-yellow-800">
                <Clock className="mr-1 h-3 w-3" />
                Pending
              </span>
            </div>
          );
        }
      } else {
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-red-100 text-red-800">
              <XCircle className="mr-1 h-3 w-3" />
              Not Generated
            </span>
          </div>
        );
      }
    },
    sortingFn: (rowA, rowB) => {
      const statusA = rowA.getValue("payslipStatus") as string;
      const statusB = rowB.getValue("payslipStatus") as string;

      if (statusA === "not_generated" && statusB === "generated") return -1;
      if (statusA === "generated" && statusB === "not_generated") return 1;
      return 0;
    },
  },
  {
    id: "netSalary",
    header: "Net Salary",
    cell: ({ row }) => {
      const employee = row.original;
      if (employee.payslipStatus === "generated" && employee.payslip) {
        return (
          <div className="text-left font-medium">
            ₹{employee.payslip.netSalary.toLocaleString()}
          </div>
        );
      }
      return <div className="text-muted-foreground text-left">—</div>;
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const employee = row.original;
      return (
        <PayslipActionsCell
          employee={employee}
          month={month}
          year={year}
          onUpdate={onUpdate}
        />
      );
    },
  },
];
